import React from 'react';
import { FaUser, FaProjectDiagram, FaChevronDown, FaPlus } from 'react-icons/fa';
import BusinessPartnerService, { BusinessPartner } from '../../contacts/services/BusinessPartnerService';
import { Project } from '../../contacts/services/ProjectService';

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '6px',
  fontSize: '13px',
  fontWeight: 600,
  color: '#444',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #e0e3ef',
  borderRadius: '6px',
  fontSize: '14px',
  boxSizing: 'border-box',
};

interface OfferFormSectionProps {
  isEdit: boolean;
  partner: BusinessPartner | null;
  project: Project | null;
  partnerSearch: string;
  onPartnerSearchChange: (val: string) => void;
  partnerOptions: BusinessPartner[];
  partnerDropdownOpen: boolean;
  onPartnerDropdownToggle: () => void;
  loadingPartners: boolean;
  onPartnerSelect: (p: BusinessPartner) => void;
  onClearPartner: () => void;
  partnerDropdownRef: React.RefObject<HTMLDivElement | null>;
  projectSearch: string;
  onProjectSearchChange: (val: string) => void;
  projectOptions: Project[];
  projectDropdownOpen: boolean;
  onProjectDropdownToggle: () => void;
  loadingProjects: boolean;
  onProjectSelect: (p: Project) => void;
  onClearProject: () => void;
  projectDropdownRef: React.RefObject<HTMLDivElement | null>;
  onOpenProjectModal: () => void;
  title: string;
  onTitleChange: (val: string) => void;
  validUntil: string;
  onValidUntilChange: (val: string) => void;
  paymentTermsDays: string;
  onPaymentTermsDaysChange: (val: string) => void;
  terms: string;
  onTermsChange: (val: string) => void;
}

export default function OfferFormSection({
  isEdit,
  partner,
  project,
  partnerSearch,
  onPartnerSearchChange,
  partnerOptions,
  partnerDropdownOpen,
  onPartnerDropdownToggle,
  loadingPartners,
  onPartnerSelect,
  onClearPartner,
  partnerDropdownRef,
  projectSearch,
  onProjectSearchChange,
  projectOptions,
  projectDropdownOpen,
  onProjectDropdownToggle,
  loadingProjects,
  onProjectSelect,
  onClearProject,
  projectDropdownRef,
  onOpenProjectModal,
  title,
  onTitleChange,
  validUntil,
  onValidUntilChange,
  paymentTermsDays,
  onPaymentTermsDaysChange,
  terms,
  onTermsChange,
}: OfferFormSectionProps): React.JSX.Element {
  return (
    <div
      style={{
        background: 'white',
        border: '1px solid #e0e3ef',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
      }}
    >
      {/* Customer Dropdown */}
      <div style={{ marginBottom: '24px' }}>
        <label style={labelStyle}>
          Customer <span style={{ color: '#dc2626' }}>*</span>
        </label>
        {isEdit && partner ? (
          <div
            style={{
              ...inputStyle,
              background: '#f8f9fc',
              color: '#666',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <FaUser size={14} color="#666" />
            {BusinessPartnerService.getDisplayName(partner)}
          </div>
        ) : (
          <div ref={partnerDropdownRef} style={{ position: 'relative' }}>
            <div
              style={{
                ...inputStyle,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                background: 'white',
              }}
              onClick={onPartnerDropdownToggle}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                <FaUser size={14} color="#666" />
                {partner ? (
                  <span style={{ color: '#333' }}>{BusinessPartnerService.getDisplayName(partner)}</span>
                ) : (
                  <span style={{ color: '#999' }}>Select customer...</span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {partner && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onClearPartner();
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: '4px',
                      cursor: 'pointer',
                      color: '#666',
                      fontSize: '12px',
                    }}
                  >
                    ✕
                  </button>
                )}
                <FaChevronDown size={12} color="#666" />
              </div>
            </div>

            {partnerDropdownOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: 'white',
                  border: '1px solid #e0e3ef',
                  borderRadius: '6px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  zIndex: 100,
                  maxHeight: '300px',
                  overflow: 'auto',
                }}
              >
                <div style={{ padding: '8px' }}>
                  <input
                    type="text"
                    value={partnerSearch}
                    onChange={(e) => onPartnerSearchChange(e.target.value)}
                    placeholder="Search customers..."
                    style={{
                      ...inputStyle,
                      padding: '8px 12px',
                      fontSize: '13px',
                    }}
                    autoFocus
                  />
                </div>

                {loadingPartners ? (
                  <div style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
                    Loading...
                  </div>
                ) : partnerOptions.length === 0 ? (
                  <div style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
                    No customers found
                  </div>
                ) : (
                  <div>
                    {partnerOptions.map((p) => (
                      <div
                        key={p.id}
                        onClick={() => onPartnerSelect(p)}
                        style={{
                          padding: '10px 16px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f0f0f0',
                          background: partner?.id === p.id ? '#f0f4ff' : 'white',
                        }}
                        onMouseEnter={(e) => {
                          (e.target as HTMLElement).style.background = '#f8f9fc';
                        }}
                        onMouseLeave={(e) => {
                          (e.target as HTMLElement).style.background =
                            partner?.id === p.id ? '#f0f4ff' : 'white';
                        }}
                      >
                        <div style={{ fontWeight: 600, color: '#021a4a' }}>
                          {BusinessPartnerService.getDisplayName(p)}
                        </div>
                        {p.city && (
                          <div style={{ fontSize: '12px', color: '#666' }}>{p.city}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Project (optional dropdown) */}
      {partner && !isEdit && (
        <div style={{ marginBottom: '24px' }}>
          <label style={labelStyle}>Project (optional)</label>
          <div ref={projectDropdownRef} style={{ position: 'relative' }}>
            <div
              style={{
                ...inputStyle,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                background: 'white',
              }}
              onClick={onProjectDropdownToggle}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                <FaProjectDiagram size={14} color="#666" />
                {project ? (
                  <span style={{ color: '#333' }}>{project.name}</span>
                ) : (
                  <span style={{ color: '#999' }}>Select project or leave empty</span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {project && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onClearProject();
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: '4px',
                      cursor: 'pointer',
                      color: '#666',
                      fontSize: '12px',
                    }}
                  >
                    ✕
                  </button>
                )}
                <FaChevronDown size={12} color="#666" />
              </div>
            </div>

            {projectDropdownOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: 'white',
                  border: '1px solid #e0e3ef',
                  borderRadius: '6px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  zIndex: 100,
                  maxHeight: '300px',
                  overflow: 'auto',
                }}
              >
                <div style={{ padding: '8px' }}>
                  <input
                    type="text"
                    value={projectSearch}
                    onChange={(e) => onProjectSearchChange(e.target.value)}
                    placeholder="Search projects..."
                    style={{
                      ...inputStyle,
                      padding: '8px 12px',
                      fontSize: '13px',
                    }}
                    autoFocus
                  />
                </div>

                {loadingProjects ? (
                  <div style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
                    Loading...
                  </div>
                ) : projectOptions.length === 0 ? (
                  <div style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
                    No projects found
                  </div>
                ) : (
                  <div>
                    {projectOptions.map((p) => (
                      <div
                        key={p.id}
                        onClick={() => onProjectSelect(p)}
                        style={{
                          padding: '10px 16px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f0f0f0',
                          background: project?.id === p.id ? '#f0f4ff' : 'white',
                        }}
                        onMouseEnter={(e) => {
                          (e.target as HTMLElement).style.background = '#f8f9fc';
                        }}
                        onMouseLeave={(e) => {
                          (e.target as HTMLElement).style.background =
                            project?.id === p.id ? '#f0f4ff' : 'white';
                        }}
                      >
                        <div style={{ fontWeight: 600, color: '#021a4a' }}>{p.name}</div>
                        {p.reference && (
                          <div style={{ fontSize: '12px', color: '#666' }}>{p.reference}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div
                  style={{
                    padding: '12px 16px',
                    borderTop: '1px solid #e0e3ef',
                    background: '#f8f9fc',
                  }}
                >
                  <button
                    type="button"
                    onClick={onOpenProjectModal}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      color: '#021a4a',
                      fontSize: '13px',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <FaPlus size={10} />
                    Create new project
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Basic Info */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '24px', marginBottom: '24px' }}>
        <div>
          <label style={labelStyle}>
            Title <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
            placeholder="e.g. Offer for renovation"
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Valid until</label>
          <input
            type="date"
            value={validUntil}
            onChange={(e) => onValidUntilChange(e.target.value)}
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Payment terms (days)</label>
          <input
            type="number"
            min={0}
            value={paymentTermsDays}
            onChange={(e) => onPaymentTermsDaysChange(e.target.value)}
            placeholder="e.g. 14"
            style={inputStyle}
          />
          <span style={{ fontSize: '11px', color: '#666', marginTop: '4px', display: 'block' }}>
            Leave empty for tenant default
          </span>
        </div>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <label style={labelStyle}>Payment terms</label>
        <textarea
          value={terms}
          onChange={(e) => onTermsChange(e.target.value)}
          rows={2}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </div>
    </div>
  );
}
